import React from 'react';
import classnames from 'classnames';
import PopperPositionedPopover from '@wix/da-ds/pkg/popper/PopperPositionedPopover';
import PopoverWrapper from '@wix/da-ds/pkg/popper/PopoverWrapper';
import SimpleTooltipContent from '@wix/da-ds/pkg/tooltips/SimpleTooltipContent';
import { useReverseTheme } from '@wix/da-react-context/pkg/ThemeContext';
import type { OffsetModifier, ReferenceChildrenProps } from '@wix/da-ds/pkg/popper/types';
import popoverStyles from './PopoverWithTooltip.scss';

export interface Props {
  tooltip?: string;
  arrowClassName?: string;
  contentClassName?: string;
  referenceClassName?: string;
  children?: React.ReactNode;
  tooltipOffset?: OffsetModifier;
  onReferenceClick?: (e: React.MouseEvent) => void;
  /**
   * ONLY USE THIS IF YOU KNOW WHAT YOU ARE DOING
   * By default, children passed to this component will be the reference element for the popper,
   * or alternatively you can pass a renderReference prop. This is helpful if you need to make the
   * reference element be some inner node on the entire structure of the tooltip.
   */
  renderReference?: (props: ReferenceChildrenProps) => React.ReactNode;
}

const TooltipPopover: React.FC<Props> = ({
  tooltip,
  arrowClassName,
  contentClassName,
  referenceClassName,
  children,
  tooltipOffset = [0, 8],
  onReferenceClick,
  renderReference,
}) => {
  const reverseTheme = useReverseTheme();

  return (
    <PopperPositionedPopover
      renderPopover={({
        effectivePlacement,
        effectiveSecondaryPlacement,
        rootRef,
        arrowRef,
        contentStyle,
        arrowStyle,
        contentAttributes,
        arrowAttributes,
        updatePositioning,
      }) => (
        <PopoverWrapper
          effectivePlacement={effectivePlacement}
          effectiveSecondaryPlacement={effectiveSecondaryPlacement}
          rootRef={rootRef}
          arrowRef={arrowRef}
          contentStyle={contentStyle}
          arrowStyle={arrowStyle}
          updatePositioning={updatePositioning}
          contentAttributes={contentAttributes}
          arrowAttributes={arrowAttributes}
          forceTheme={reverseTheme}
          contentClassName={classnames(contentClassName, popoverStyles['new-tooltip'])}
        >
          <SimpleTooltipContent>{tooltip}</SimpleTooltipContent>
        </PopoverWrapper>
      )}
      className={referenceClassName}
      onReferenceClick={onReferenceClick}
      offsetModifier={tooltipOffset}
      renderReference={renderReference}
    >
      {children}
    </PopperPositionedPopover>
  );
};
TooltipPopover.displayName = 'TooltipPopover';

export default TooltipPopover;
